import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, ScaleFade, chakra } from '@chakra-ui/core';
import { Tr, Th, Td, Thead, Tbody, Table } from '@chakra-ui/table';
import { IoChevronDown, IoChevronUp } from 'react-icons/io5';
import { MdOutlineUnfoldMore } from 'react-icons/md';
import omit from 'lodash/omit';
import { useSortBy, useTable, usePagination, Column } from 'react-table';
import {
  mainTable,
  tableHeading,
} from '../../recruiterManagement/recruiterDashboard/recruitersInfoTable/RecruiterInfoTableStyle';
import { TableStyle } from '../styles';

interface DataTableProps<T extends object> {
  columns: Column<T>[];
  data: T[];
  getRowProps?: (row: any) => any;
  isSpanningRow?: (original: T) => boolean;
  renderSpanningRow?: (original: T) => React.ReactNode;
}

export function DataTable<T extends object>({ columns, data, getRowProps, isSpanningRow, renderSpanningRow }: DataTableProps<T>) {
  const { t } = useTranslation('accountParameter');
  const table = useTable<T>({ columns, data }, useSortBy, usePagination) as any;
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = table;

  return (
    <ScaleFade initialScale={0.7} in unmountOnExit={false}>
      <Table {...getTableProps()} className="mainTable simple-bordered-table" key="mainTable" css={mainTable}>
        <Thead css={tableHeading} className="tableHeading">
          {headerGroups.map((headerGroup: any) => (
            <Tr {...headerGroup.getHeaderGroupProps()} textAlign="left" key={headerGroup.getHeaderGroupProps().key}>
              {headerGroup.headers.map((column: any) => (
                <Th
                  {...omit(column.getHeaderProps(column.getSortByToggleProps?.()), 'title')}
                  isNumeric={column.isNumeric}
                  width={column.width}
                  key={column.getHeaderProps().key}
                  align="left"
                >
                  <Box display="flex" width="fit-content">
                    {column.render('Header')}
                    {!column?.disableSortBy && (
                      <chakra.span pl="4">
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <IoChevronDown aria-label={t('dialogFlowAgentsManager.sortedDescending')} />
                          ) : (
                            <IoChevronUp aria-label={t('dialogFlowAgentsManager.sortedAscending')} />
                          )
                        ) : (
                          <MdOutlineUnfoldMore aria-label={t('dialogFlowAgentsManager.unsorted')} />
                        )}
                      </chakra.span>
                    )}
                  </Box>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {rows.map((row: any) => {
            prepareRow(row);
            const rowProps = getRowProps ? getRowProps(row) : {};
            const spanning = isSpanningRow ? isSpanningRow(row.original) : false;

            if (spanning) {
              return (
                <Tr
                  css={TableStyle}
                  {...row.getRowProps()}
                  {...rowProps}
                  key={row.getRowProps().key}
                  className={`${rowProps.className || ''} section-row`}
                  cursor="unset"
                >
                  <Td colSpan={row.cells.length} px={5} py={3} backgroundColor="gray.50">
                    {renderSpanningRow ? renderSpanningRow(row.original) : null}
                  </Td>
                </Tr>
              );
            }

            return (
              <Tr
                css={TableStyle}
                {...row.getRowProps()}
                {...rowProps}
                key={row.getRowProps().key}
                cursor="pointer"
                className={`${rowProps.className || ''}`}
              >
                {row.cells.map((cell: any) => (
                  <Td {...cell.getCellProps()} sNumeric={cell.column.isNumeric} key={cell.getCellProps().key} px={5} py={3}>
                    {cell.render('Cell')}
                  </Td>
                ))}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </ScaleFade>
  );
}
